// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductImageCarouselStyles } from "@insite/content-library/Components/ProductImageCarousel";
import Badge from "@insite/mobius/Badge";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VideoContainer from "@steiner/App/Components/Media/VideoContainer";
import setSelectedVideoIndex from "@steiner/App/Handlers/ProductDetails/SetSelectedVideoIndex";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { getProductVideos, getSelectedVideo, getSelectedVideoIndex } from "@steiner/App/Store/Selectors/ProductDetailsSelector";
import ProductImageCarousel from "@steiner/Overrides/Components/ProductImageCarousel";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@steiner/Overrides/Components/ProductPrimaryImage";
import { parseBoolean } from "@steiner/XDL/Utilities";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomAppState, ownProps: HasProductContext) => ({
    selectedImage: getSelectedImage(state, ownProps.productContext.product),
    selectedImageIndex: state.pages.productDetails.selectedImageIndex,
    // XNG-Change
    selectedVideo: getSelectedVideo(state, ownProps.productContext.product),
    selectedVideoIndex: getSelectedVideoIndex(state, ownProps.productContext.product),
    videos: getProductVideos(state, ownProps.productContext.product),
});

const mapDispatchToProps = {
    setSelectedImageIndex,
    // XNG-Change
    setSelectedVideoIndex,
};

export interface ProductDetailsPrimaryImageStyles {
    /**
     * @deprecated Use primaryImage styles instead.
     */
    centeringWrapper?: InjectableCss;
    hiddenWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    image?: LazyImageProps;

    primaryImage?: ProductPrimaryImageStyles;
    fullPrimaryImageModal?: ModalPresentationProps;
    fullPrimaryImage?: ProductPrimaryImageStyles;
    imageCarousel?: ProductImageCarouselStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    
    // XNG-Change
    videoContainer?: InjectableCss;
    modalImageContainer?: InjectableCss;
}

export const primaryImageStyles: ProductDetailsPrimaryImageStyles = {
    fullPrimaryImageModal: {
        cssOverrides: {
            modalContainer: css`
                width: auto;
            `,
            modalBody: css`
                width: auto;
                max-width: initial;
                max-height: initial !important;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 10px 10px 0;
            `,
            modalContent: css`
                text-align: center;
            `,
        },
    },
    fullPrimaryImage: {
        image: {
            css: css`
                img {
                    width: auto;
                    max-height: 70vh;
                }
            `,
        },
        img: {
            css: css`
                width: auto;
                max-height: 70vh;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const styles = primaryImageStyles;

const ProductDetailsPrimaryImage = ({
    productContext,
    selectedImage,
    selectedImageIndex,
    setSelectedImageIndex,
    // XNG-Change
    selectedVideo,
    selectedVideoIndex,
    setSelectedVideoIndex,
    videos,
}: Props) => {
    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = useState(false);
    const imageClickHandler = () => {
        setFullImageModalIsOpen(true);
    };
    const fullPrimaryImageModalCloseHandler = () => {
        setFullImageModalIsOpen(false);
    };

    const selectImageHandler = (index: number) => {
        setSelectedImageIndex({ index });
        // XNG-Change
        if (videos && videos.length > 0) {
            setSelectedVideoIndex({ index: -1, product });
        }
    };

    const selectVideoHandler = (index: number) => {
        setSelectedImageIndex({ index: -1 });
        setSelectedVideoIndex({ index, product });
    };

    const { product } = productContext;
    if (!product) {
        return null;
    }

    const image =
        selectedImage ??
        ({
            id: product.id,
            imageAltText: product.imageAltText,
            imageType: "Static",
            largeImagePath: product.largeImagePath,
            mediumImagePath: product.mediumImagePath,
            smallImagePath: product.smallImagePath,
        } as ImageModel);

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: true,
        showImageBadge: true,
        styles,
    });

    const path = image.mediumImagePath || image.largeImagePath;

    // XNG-Change
    const video = selectedVideo;

    if (!path) {
        return null;
    }

    const videoSection = video && (
        <StyledWrapper {...styles.videoContainer}>
            <VideoContainer
                src={video.videoLink as string}
                isYoutubeVideo={parseBoolean(video.isYoutubeVideo)}
                extension={getFileExtension(video.videoLink as string)}
                controls={true}
                insideModal={fullPrimaryImageModalIsOpen}
            ></VideoContainer>
        </StyledWrapper>
    );

    return (
        <>
            {videoSection}
            {!video && (
                <>
                    {!showOverlayBadge && (
                        <ProductPrimaryImage
                            product={product}
                            image={image}
                            onClick={imageClickHandler}
                            extendedStyles={styles.primaryImage}
                            useZoom={true}
                        />
                    )}
                    {showOverlayBadge && overlayBadge && (
                        <StyledWrapper {...styles.badgeAreaWrapper} ref={badgeAreaWrapperRef}>
                            <ProductPrimaryImage
                                product={product}
                                image={image}
                                onClick={imageClickHandler}
                                extendedStyles={styles.primaryImage}
                                useZoom={true}
                            />
                            <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                                <Badge
                                    type={overlayBadge.badgeType}
                                    value={
                                        overlayBadge.badgeType === "Image"
                                            ? overlayBadge.largeImageBadgePath
                                            : overlayBadge.displayText
                                    }
                                    badgeTextColor={overlayBadge.textColorHexCode}
                                    badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                    badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                    imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                                    textBadgeCss={styles.badgeText}
                                    imageBadgeProps={styles.badgeImageProps}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </>
            )}
            <Modal
                {...styles.fullPrimaryImageModal}
                isOpen={fullPrimaryImageModalIsOpen}
                handleClose={fullPrimaryImageModalCloseHandler}
            >
                <>
                    {/* XNG-Change */}
                    {videoSection}
                    {!video && (
                        <ProductPrimaryImage
                            product={product}
                            image={image}
                            useLargeImage={true}
                            extendedStyles={styles.fullPrimaryImage}
                        />
                    )}
                </>
                {product.images && product.images.length > 1 && (
                    <ProductImageCarousel
                        selectedIndex={selectedImageIndex}
                        onSelectImage={selectImageHandler}
                        extendedStyles={styles.imageCarousel}
                        // XNG-Changes
                        selectedVideoIndex={selectedVideoIndex}
                        onSelectVideo={selectVideoHandler}
                    />
                )}
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsPrimaryImage)),
    definition: {
        displayName: "Primary Image",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
